import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { BrandPlanScheduleContent } from 'types'
import { scheduleKeys } from './scheduleKeys'

interface Props {
  scheduleId: number
  contentId: number
  fileKey?: string | null
}

export type Variables = Partial<Omit<BrandPlanScheduleContent, 'id' | 'created_at'>>

interface Data {
  content: BrandPlanScheduleContent
}

interface Context {
  previousContent?: BrandPlanScheduleContent
}

export type Options = Omit<UseMutationOptions<Data, Error, Variables, Context>, 'mutationKey' | 'mutationFn'>

export const updateBrandPlanScheduleContent = async (
  scheduleId: number,
  contentId: number,
  variables: Variables,
): Promise<Data> => {
  const response = await sendToApi<Data>(`/schedules/${scheduleId}/contents/${contentId}`, {
    method: 'PUT',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export const useUpdateBrandPlanScheduleContent = ({ scheduleId, contentId, fileKey }: Props, options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, Error, Variables, Context>(
    scheduleKeys.contentDetail(scheduleId, contentId),
    (variables) => updateBrandPlanScheduleContent(scheduleId, contentId, variables),
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: scheduleKeys.contentDetail(scheduleId, contentId) })

        const previousContent = queryClient.getQueryData<BrandPlanScheduleContent>(
          scheduleKeys.contentDetail(scheduleId, contentId),
        )

        if (!previousContent) {
          return undefined
        }

        queryClient.setQueryData<BrandPlanScheduleContent>(scheduleKeys.contentDetail(scheduleId, contentId), {
          ...previousContent,
          ...variables,
        })

        return { previousContent }
      },
      onError: (_err, _variables, context) => {
        if (context?.previousContent) {
          queryClient.setQueryData<BrandPlanScheduleContent>(
            scheduleKeys.contentDetail(scheduleId, contentId),
            context.previousContent,
          )
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(scheduleKeys.contentDetail(scheduleId, contentId))
        queryClient.invalidateQueries(scheduleKeys.contents(scheduleId))
        queryClient.invalidateQueries(scheduleKeys.detail(scheduleId))
        if (fileKey) {
          queryClient.invalidateQueries(filesKeys.detail(workspaceId, fileKey))
        }
      },
      ...options,
    },
  )
}
